import { SortDirection } from '@material-ui/core';
import BaseApi, { FetchMethodType } from 'api/BaseApi';
import { BorrowerType } from 'product_modules/enums/BorrowerType';
import { DataViewSortingType, FormLayoutData, VariableValue } from 'product_modules/api/Types';
import { IBaseAccountParams } from './base/BaseAccountsApi';

export enum ApplicationStatusType {
  Custom = 'custom',
  Initial = 'initial',
  Approve = 'approve',
  Reject = 'reject'
}

export interface IApplicationStatus {
  id: string;
  name: string;
  type: ApplicationStatusType;
}

export interface IApplicationViewModel {
  id: string;
  displayId: string;
  status: IApplicationStatus;
  productId: string;
  productName: string;
  createdAt: string;
  incompleteTasksCount: number;
  borrowerId: string;
  intermediaryId?: string;
  variables: FormLayoutData;
  updatedAt: string;
  borrower: IBaseAccountParams;
  coborrowerTypes: BorrowerType[];
  coborrowerIds: string[];
  borrowerType: BorrowerType;
  statusDate?: string;
  loanAmount?: number;
  rejectedAt?: Date;
  approvedAt?: Date;
  declineReasons?: string[];
}

interface IGetBorrowerApplicationsRequest {
  count: number;
  offset: number;
  sortField: ApplicationSortingField;
  sortDirection: SortDirection;
  search?: string;
  productId?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  onlyInFinalStatus?: boolean;
  onlyInProgress?: boolean;
  onlyInApprovedStatus?: boolean;
  onlyInRejectedStatus?: boolean;
}

export interface IGetBorrowerApplicationsResponse {
  applications: IApplicationViewModel[];
  total: number;
}

export interface ICreateBorrowerParams {
  type: BorrowerType;
  variables: Record<string, VariableValue>;
}

export interface ICreateApplicationParams {
  productId: string;
  coborrowers?: ICreateBorrowerParams[];
  intermediaryId?: string;
}

export interface ICreateApplicationByIntermediaryParams {
  productId: string;
  borrower: ICreateBorrowerParams;
  coborrowers?: ICreateBorrowerParams[];
}

export enum ApplicationSortingField {
  CreatedAt = 'createdAt',
  DisplayId = 'displayId',
  LoanAmount = 'loanAmount',
  BorrowerFullName = 'borrowerFullName',
  Email = 'borrowerEmail',
  Phone = 'borrowerPhoneNumber',
}

export type ApplicationsSortingType = DataViewSortingType<ApplicationSortingField>;

export interface IApplicationsApi {
  getBorrowerApplications(params: IGetBorrowerApplicationsRequest): Promise<IGetBorrowerApplicationsResponse>;
  getCurrentApplication(): Promise<IApplicationViewModel | null>;
  createApplication(params: ICreateApplicationParams): Promise<IApplicationViewModel>;
}

export default class ApplicationsRestApi extends BaseApi implements IApplicationsApi {
  public getBorrowerApplications(params: IGetBorrowerApplicationsRequest): Promise<IGetBorrowerApplicationsResponse> {
    const urlParams = new URLSearchParams();

    for (const param of Object.keys(params)) {
      const paramValue = params[param as keyof IGetBorrowerApplicationsRequest];

      if (paramValue !== undefined) {
        urlParams.append(param, paramValue.toString());
      }
    }

    return this.fetch(`/applications?${urlParams}`);
  }

  public async getCurrentApplication(): Promise<IApplicationViewModel | null> {
    const { application } = await this.fetch<{ application: IApplicationViewModel | null }>('/applications/current');

    return application;
  }

  public createApplication(params: ICreateApplicationParams): Promise<IApplicationViewModel> {
    const { productId } = params;

    return this.fetch('/applications', {
      method: FetchMethodType.POST,
      body: {
        productId,
        variables: {},
        coborrowers: params.coborrowers,
        intermediaryId: params.intermediaryId,
      },
    });
  }

  public createApplicationByIntermediary(params: ICreateApplicationByIntermediaryParams): Promise<IApplicationViewModel> {
    const { productId } = params;

    return this.fetch('/applications', {
      method: FetchMethodType.POST,
      body: {
        productId,
        variables: {},
        borrower: params.borrower,
        coborrowers: params.coborrowers,
      },
    });
  }

  public findByDisplayId(displayId: string) {
    return this.fetch<IApplicationViewModel>(`/applications/${displayId}`);
  }
}
